import React, { useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import RadixDialog from '../ui/RadixDialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import PasswordFields, { validatePassword } from './PasswordFields';
import {
  createExtPartner,
  updateExtPartnerPassword,
} from '../../services/extPartnerAdmin';

export default function PartnerAccountDialog({
  user,
  onClose,
  onSaved,
  onCloseAutoFocus,
}) {
  const [form, setForm] = useState({
    partnerName: '',
    name: '',
    email: '',
    units: '100',
    password: '',
    confirmation: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const updating = Boolean(user);
  const change = (field, value) =>
    setForm((previous) => ({ ...previous, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    setError('');
    const passwordError = validatePassword(form.password, form.confirmation);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (
      !updating &&
      (!form.partnerName.trim() || !form.name.trim() || !form.email.trim())
    ) {
      setError('Company name, user name and login email are required.');
      return;
    }
    const units = Number(form.units);
    if (
      !updating &&
      (form.units === '' ||
        !Number.isInteger(units) ||
        units < 0 ||
        units > 2147483647)
    ) {
      setError(
        'Web check-in allowance must be a whole number from 0 to 2147483647.',
      );
      return;
    }
    inFlight.current = true;
    setSaving(true);
    try {
      if (updating) {
        await updateExtPartnerPassword(user.id, form.password);
      } else {
        await createExtPartner({
          partnerName: form.partnerName.trim(),
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          units,
        });
      }
      setForm((previous) => ({ ...previous, password: '', confirmation: '' }));
      onSaved(
        updating
          ? `Password updated for ${user.email}.`
          : 'Partner and login created successfully.',
      );
    } catch (err) {
      setError(err.message || 'Unable to save. Please try again.');
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <RadixDialog
      open
      onCloseAutoFocus={onCloseAutoFocus}
      onOpenChange={(open) => {
        if (!open && !inFlight.current) onClose();
      }}
      title={updating ? 'Update password' : 'Create partner'}
      showCloseButton={!saving}
      description={
        updating
          ? `Set a new login password for ${user.email}.`
          : 'Create a company and its first admin login.'
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <fieldset disabled={saving} className="space-y-4">
          {!updating && (
            <>
              <div>
                <label
                  htmlFor="partner-company"
                  className="block text-sm font-medium mb-1"
                >
                  Company name
                </label>
                <Input
                  id="partner-company"
                  value={form.partnerName}
                  required
                  maxLength={255}
                  autoComplete="organization"
                  onChange={(event) =>
                    change('partnerName', event.target.value)
                  }
                />
              </div>
              <div>
                <label
                  htmlFor="partner-user-name"
                  className="block text-sm font-medium mb-1"
                >
                  User name
                </label>
                <Input
                  id="partner-user-name"
                  value={form.name}
                  required
                  maxLength={255}
                  autoComplete="name"
                  onChange={(event) => change('name', event.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="partner-email"
                  className="block text-sm font-medium mb-1"
                >
                  Login email
                </label>
                <Input
                  id="partner-email"
                  type="email"
                  value={form.email}
                  required
                  maxLength={254}
                  autoComplete="off"
                  onChange={(event) => change('email', event.target.value)}
                />
              </div>
            </>
          )}
          <PasswordFields
            password={form.password}
            confirmation={form.confirmation}
            onChange={change}
            disabled={saving}
          />
          {!updating && (
            <div>
              <label
                htmlFor="partner-allowance"
                className="block text-sm font-medium mb-1"
              >
                Web check-in allowance
              </label>
              <Input
                id="partner-allowance"
                type="number"
                value={form.units}
                required
                min={0}
                max={2147483647}
                step={1}
                onChange={(event) => change('units', event.target.value)}
              />
            </div>
          )}
        </fieldset>
        {error && (
          <p
            role="alert"
            className="text-sm text-red-700 bg-red-50 rounded-md p-3"
          >
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving && <Loader2 className="animate-spin" />}
            {saving
              ? 'Saving…'
              : updating
                ? 'Update password'
                : 'Create partner'}
          </Button>
        </div>
      </form>
    </RadixDialog>
  );
}
